import { NgStyle } from '@angular/common';
import { AfterViewInit, Component, effect, input, signal } from '@angular/core';
import { Control, control, layerGroup, Map, map, tileLayer, HeatLayer } from 'leaflet';
import { OSMResponse } from '../../api/portal/portal.service.interface';
import { centerMap, createHeatPoint, drawLine, drawPoint, drawPolygon, groupByType } from './map-util';
import 'leaflet.heat';
import * as L from 'leaflet';
import { ControlSliderComponent } from './components/control-slider/control-slider.component';

@Component({
  selector: 'app-map',
  imports: [NgStyle, ControlSliderComponent],
  templateUrl: './map.component.html',
  styleUrl: './map.component.css',
})
export class MapComponent implements AfterViewInit {
  private readonly mapReady = signal(false);
  public width = input<string>('100%');
  public height = input<string>('500px');
  public features = input<OSMResponse>();
  protected isLoading = false;
  private map!: Map;
  protected heatLayer = signal<HeatLayer | null>(null);

  private initMap() {
    this.map = map('map', {
      center: [56.47765, 10.0725], // Denmark
      zoom: 7,
    });

    const tiles = tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      minZoom: 3,
      attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    });

    tiles.addTo(this.map);
  }

  constructor() {
    effect(() => {
      if (!this.mapReady()) return;

      const features = this.features()?.features;
      if (features && features.length > 0) {
        const colors = ['black', 'red', 'blue'];
        const coordinates: [number, number][] = [];
        const heatCoordinates: [number, number, number][] = [];
        const layers = {} as Control.LayersObject;
        const types = groupByType(features);

        types.forEach((type, idx) => {
          const color = colors[idx % colors.length];
          const layer = layerGroup([]);
          layers[type[0].properties.type] = layer;
          layer.addTo(this.map);

          type.forEach((feature) => {
            if (feature.geometry.type === 'Point') {
              drawPoint(layer, feature.geometry.coordinates, color);
              coordinates.push(feature.geometry.coordinates);
            } else if (feature.geometry.type === 'HeatPoint') {
              heatCoordinates.push(createHeatPoint(feature.geometry.coordinates));
              coordinates.push(feature.geometry.coordinates);
            } else if (feature.geometry.type === 'LineString') {
              drawLine(layer, feature.geometry.coordinates, color);
              coordinates.push(...feature.geometry.coordinates);
            } else if (feature.geometry.type === 'Polygon') {
              feature.geometry.coordinates.forEach((polygons) => {
                drawPolygon(layer, polygons, color);
                coordinates.push(...polygons);
              });
            } else {
              throw new Error('unknown geometry type');
            }
          });
        });
        if (heatCoordinates.length > 0) {
          const heatLayer = L.heatLayer(heatCoordinates, { radius: 10 });

          this.heatLayer.set(heatLayer);
          layers['Heat'] = heatLayer.addTo(this.map);
        }
        control.layers(undefined, layers).addTo(this.map);
        centerMap(this.map, coordinates);
      }
    });
  }

  ngAfterViewInit() {
    this.initMap();
    this.mapReady.set(true);
  }

  protected onRadiusChange(radius: number) {
    const heatLayer = this.heatLayer();
    if (heatLayer && heatLayer.setOptions) {
      heatLayer.setOptions({ radius: radius });
    }
  }
}
